import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Play, AlertTriangle, CheckCircle2 } from "lucide-react";
import { getAuthUrl } from "@/utils/redirectUtils";
import { Link } from "react-router-dom";

const KOHeroSection = () => {
  const wastedBars = [
    { label: "roofing company", cost: "$45.10", width: "92%", color: "bg-red-500" },
  ];

  const optimisedBars = [
    { label: "roof repair near me", cost: "$8.50", width: "68%" },
    { label: "emergency roof leak", cost: "$11.20", width: "52%" },
    { label: "storm damage roof", cost: "$9.10", width: "44%" },
    { label: "affordable roof repair", cost: "$5.80", width: "36%" },
    { label: "shingle replacement", cost: "$5.60", width: "28%" },
    { label: "roof inspection cost", cost: "$4.30", width: "20%" },
  ];

  return (
    <section className="pt-32 pb-24 px-4 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute inset-0 bg-gradient-to-b from-background to-primary/5" />
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)",
          backgroundSize: "40px 40px",
        }}
      />
      <div className="absolute top-20 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-primary/3 rounded-full blur-3xl" />

      <div className="container mx-auto max-w-6xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: Text */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Badge className="px-4 py-1.5 text-sm bg-primary/10 text-primary border-primary/20 mb-6">
                Budget-Smart Keyword Selection
              </Badge>
            </motion.div>

            <motion.h1
              className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              Stop Wasting Budget on Keywords{" "}
              <span className="text-primary">You Can't Afford</span>
            </motion.h1>

            <motion.p
              className="text-lg md:text-xl text-muted-foreground mb-8 leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              Upfixe compares your daily budget and target CPA against every keyword's bid estimate,
              filters for the ones you can actually afford, prioritises by search volume, and organises
              them into thematic ad groups — so your ads are effective instead of burning everything on
              one expensive click.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
            >
              <Button
                size="lg"
                className="h-14 px-8 text-lg"
                onClick={() => window.location.href = getAuthUrl()}
              >
                Optimise My Keywords <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="h-14 px-8 text-lg"
                asChild
              >
                <Link to="/how-it-works">
                  <Play className="mr-2 h-5 w-5" /> See How It Works
                </Link>
              </Button>
            </motion.div>

            <motion.p
              className="text-sm text-muted-foreground mt-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.6 }}
            >
              No credit card required. Free campaign included.
            </motion.p>
          </div>

          {/* Right: Budget Visualization */}
          <motion.div
            className="relative"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <div className="bg-white rounded-2xl shadow-2xl border border-border overflow-hidden">
              {/* Wasted Budget */}
              <div className="p-6 border-b border-border bg-red-50/50">
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle className="h-5 w-5 text-red-500" />
                  <span className="text-sm font-semibold text-red-700">Typical Approach — $50/day budget</span>
                </div>
                {wastedBars.map((bar, i) => (
                  <motion.div
                    key={bar.label}
                    className="mb-2"
                    initial={{ opacity: 0, width: 0 }}
                    animate={{ opacity: 1, width: "100%" }}
                    transition={{ duration: 0.8, delay: 0.8 + i * 0.1 }}
                  >
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium text-red-800">{bar.label}</span>
                      <span className="text-red-600 font-bold">{bar.cost}/click</span>
                    </div>
                    <div className="h-8 bg-red-100 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-red-500 rounded-full flex items-center justify-end pr-3"
                        initial={{ width: 0 }}
                        animate={{ width: bar.width }}
                        transition={{ duration: 1.2, delay: 1.0, ease: "easeOut" }}
                      >
                        <span className="text-[10px] font-bold text-white">1 click = budget gone</span>
                      </motion.div>
                    </div>
                  </motion.div>
                ))}
                <motion.p
                  className="text-xs text-red-600 mt-3 font-medium"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 2.0 }}
                >
                  Budget exhausted by 10:15 AM. 1 click. No conversions.
                </motion.p>
              </div>

              {/* Optimised Budget */}
              <div className="p-6 bg-green-50/30">
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-semibold text-green-800">Upfixe Approach — Same $50/day budget</span>
                </div>
                {optimisedBars.map((bar, i) => (
                  <motion.div
                    key={bar.label}
                    className="mb-2"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.4, delay: 1.4 + i * 0.12 }}
                  >
                    <div className="flex justify-between text-xs mb-1">
                      <span className="font-medium text-green-800">{bar.label}</span>
                      <span className="text-green-600 font-bold">{bar.cost}/click</span>
                    </div>
                    <div className="h-5 bg-green-100 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-green-500 rounded-full"
                        initial={{ width: 0 }}
                        animate={{ width: bar.width }}
                        transition={{ duration: 0.8, delay: 1.6 + i * 0.12, ease: "easeOut" }}
                      />
                    </div>
                  </motion.div>
                ))}
                <motion.p
                  className="text-xs text-green-700 mt-3 font-medium"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 3.0 }}
                >
                  Budget spread all day. 45+ clicks. Multiple conversions.
                </motion.p>
              </div>
            </div>

            {/* Floating badges */}
            <motion.div
              className="absolute -top-3 -right-3 bg-white rounded-full px-4 py-2 shadow-lg border border-green-200"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: "spring", delay: 2.5 }}
            >
              <span className="text-sm font-bold text-green-600">+45x more clicks</span>
            </motion.div>
            <motion.div
              className="absolute -bottom-3 -left-3 bg-white rounded-full px-4 py-2 shadow-lg border border-primary/20"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ type: "spring", delay: 2.8 }}
            >
              <span className="text-sm font-bold text-primary">-87% CPC</span>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default KOHeroSection;
